import React, { useState } from 'react';

import { SourceTextDialog } from '~/components/Clive/SourceTextDialog';
import Chip from '~/components/core/Atomic/Chip/Chip';
import PreviewCard from '~/components/ui/atoms/PreviewCard';

import { ExpertReferencePreview, getVersionDate } from './ExpertReferencePreview';
import type { ReferenceTriggerProps } from './referenceKinds';
import { TEXTS } from './texts';

/**
 * ExpertReferenceTrigger
 *
 * A cited expert report: a chip naming the report and the version cited, a preview on hover, and the full
 * report on click.
 */
export const ExpertReferenceTrigger: React.FC<ReferenceTriggerProps> = ({ reference }) => {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const name = reference.name ?? '';
  const { text } = reference;
  const versionDate = getVersionDate(reference);

  const trigger = (
    <Chip
      color="primary"
      // A report that came without its text has nothing to open.
      onClick={text ? () => setIsReportOpen(true) : undefined}
      label={
        <span className="block max-w-[240px] truncate text-xs font-semibold">
          {TEXTS.EXPERT_CHIP_LABEL(name, versionDate)}
        </span>
      }
      size="small"
      className="h-20 max-w-[240px] bg-azure-200 text-azure-700 hover:bg-azure-200 hover:text-azure-700"
    />
  );

  return (
    <>
      <PreviewCard trigger={trigger}>
        <ExpertReferencePreview reference={reference} />
      </PreviewCard>

      {/* Beside the chip, not inside it: React events bubble out of the dialog's portal through the component
          tree, so a click in the dialog would reach the chip again. */}
      {isReportOpen && text && (
        <SourceTextDialog
          title={TEXTS.EXPERT_CHIP_LABEL(name, versionDate)}
          text={text}
          onClose={() => setIsReportOpen(false)}
        />
      )}
    </>
  );
};
